/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

/** Groupes RTF à ignorer intégralement : tables de polices/couleurs, métadonnées, images... */
const IGNORED_GROUPS = new Set([
  'fonttbl',
  'colortbl',
  'stylesheet',
  'info',
  'generator',
  'pict',
  'themedata',
  'colorschememapping',
]);

/** Windows-1252 pour les octets 0x80-0x9F ; 0xA0-0xFF correspondent directement à Unicode. */
const CP1252_HIGH_BYTES: Record<number, string> = {
  128: '€',
  130: '‚',
  131: 'ƒ',
  132: '„',
  133: '…',
  134: '†',
  135: '‡',
  136: 'ˆ',
  137: '‰',
  138: 'Š',
  139: '‹',
  140: 'Œ',
  142: 'Ž',
  145: '‘',
  146: '’',
  147: '“',
  148: '”',
  149: '•',
  150: '–',
  151: '—',
  152: '˜',
  153: '™',
  154: 'š',
  155: '›',
  156: 'œ',
  158: 'ž',
  159: 'Ÿ',
};

const decodeCp1252Byte = (byte: number): string => {
  if (byte >= 0x80 && byte <= 0x9f) {
    return CP1252_HIGH_BYTES[byte] ?? '';
  }
  return String.fromCharCode(byte);
};

/**
 * Convertit un texte RTF (`{\rtf1\ansi...}`) en texte brut.
 * Ce n'est pas un moteur RTF complet, seulement de quoi afficher lisiblement les mentions dont
 * le champ `texte` peut être stocké au format RTF en base : retours à la ligne (`\par`),
 * caractères accentués échappés en Windows-1252 (`\'e9`), et suppression des groupes de
 * métadonnées (table de polices, générateur...). Une chaîne qui n'est pas du RTF est retournée
 * telle quelle.
 */
const rtfToText = (value?: string | null): string => {
  if (!value) {
    return '';
  }

  if (!/^\s*\{\\rtf/i.test(value)) {
    return value;
  }

  let result = '';
  let depth = 0;
  let ignoredAtDepth: number | null = null;
  let i = 0;

  while (i < value.length) {
    const char = value[i];

    if (char === '{') {
      depth += 1;
      i += 1;
      continue;
    }

    if (char === '}') {
      if (ignoredAtDepth !== null && depth === ignoredAtDepth) {
        ignoredAtDepth = null;
      }
      depth -= 1;
      i += 1;
      continue;
    }

    if (char === '\\') {
      i += 1;
      const next = value[i];

      // Symbole "destination optionnelle" : à ignorer si le lecteur ne connaît pas le groupe
      if (next === '*') {
        ignoredAtDepth = ignoredAtDepth ?? depth;
        i += 1;
        continue;
      }

      // Caractère échappé littéral
      if (next === '\\' || next === '{' || next === '}') {
        if (ignoredAtDepth === null) {
          result += next;
        }
        i += 1;
        continue;
      }

      // Caractère hexadécimal échappé (ex. `\'e9`), décodé en Windows-1252
      if (next === "'") {
        const hex = value.slice(i + 1, i + 3);
        i += 3;
        if (ignoredAtDepth === null) {
          result += decodeCp1252Byte(parseInt(hex, 16));
        }
        continue;
      }

      // Mot de contrôle : lettres puis paramètre numérique optionnel
      const match = /^([a-zA-Z]+)(-?\d+)?/.exec(value.slice(i));
      if (match) {
        const [fullMatch, word] = match;
        i += fullMatch.length;

        // Un espace immédiatement après un mot de contrôle n'est qu'un délimiteur
        if (value[i] === ' ') {
          i += 1;
        }

        if (IGNORED_GROUPS.has(word)) {
          ignoredAtDepth = ignoredAtDepth ?? depth;
        } else if (ignoredAtDepth === null) {
          if (word === 'par' || word === 'line') {
            result += '\n';
          } else if (word === 'tab') {
            result += '\t';
          }
          // Les autres mots de contrôle (rtf, ansi, deff, fs, f, pard, viewkind...) sont des
          // instructions de mise en forme sans équivalent texte.
        }
        continue;
      }

      // Symbole de contrôle inconnu : on avance sans rien émettre
      i += 1;
      continue;
    }

    // Retours à la ligne bruts du fichier : ce sont des séparateurs de tokens, pas du texte
    if (char === '\r' || char === '\n') {
      i += 1;
      continue;
    }

    if (ignoredAtDepth === null) {
      result += char;
    }
    i += 1;
  }

  return result.trim();
};

export default rtfToText;
